import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { Loader2, Newspaper, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { clipApi } from '../api/clipApi'
import { NewsCard } from './NewsCard'
import { useDocumentMeta } from '@/lib/seo'

/**
 * Read-only view of a shared clipping.
 *
 * The destination of the share loop: whoever receives a forwarded clipping lands
 * here, sees something useful without signing up, and has one obvious next step.
 *
 * Note: meta tags are set client-side, so link unfurling in Slack or KakaoTalk will
 * show the site-wide card rather than this clipping until these routes are served as
 * pre-rendered HTML. Tracked as the remaining half of P3-3/P3-7.
 */
export function ClipPage() {
  const { code = '' } = useParams<{ code: string }>()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['clip', code],
    queryFn: () => clipApi.getClip(code),
    enabled: Boolean(code),
    retry: false,
  })

  useDocumentMeta({
    title: data ? `'${data.keyword}' 뉴스 클리핑` : '뉴스 클리핑',
    description: data
      ? `${data.keyword} 관련 기사 ${data.items.length}건을 모은 클리핑입니다.`
      : undefined,
  })

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6">
      <header className="mb-6 flex flex-wrap items-center gap-3">
        <Link to="/" className="flex items-center gap-2" aria-label="헤드라이너 홈으로">
          <span className="rounded-lg bg-primary p-2">
            <Newspaper className="h-5 w-5 text-primary-foreground" />
          </span>
          <span className="text-lg font-bold">헤드라이너</span>
        </Link>
        {data && (
          <span className="text-muted-foreground">
            · <strong className="text-foreground">{data.keyword}</strong> 클리핑 {data.items.length}
            건
          </span>
        )}
      </header>

      {isLoading && (
        <div className="flex min-h-[300px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {isError && (
        <div className="flex min-h-[300px] flex-col items-center justify-center gap-4 text-center">
          <div className="rounded-full bg-muted p-6">
            <Search className="h-12 w-12 text-muted-foreground" />
          </div>
          <div>
            <h1 className="mb-1 text-xl font-semibold">클리핑을 찾을 수 없어요</h1>
            <p className="text-muted-foreground">링크가 잘못되었거나 만료된 클리핑입니다.</p>
          </div>
          <Button asChild>
            <Link to="/">직접 키워드로 검색하기</Link>
          </Button>
        </div>
      )}

      {data && (
        <>
          {/*
            라벨이 앱의 "AI 요약"보다 긴 건 의도다. 전달받은 클리핑을 여는 사람은 요약을
            만든 버튼을 본 적이 없어서, 이 문단이 기사인지 생성물인지 화면에 적혀 있지
            않으면 알 방법이 없다. 서버 렌더 페이지(`clip_page.py`의 `AI_SUMMARY_LABEL`)와
            같은 문구를 쓴다 — 같은 클리핑이 크롤러에게와 사람에게 다르게 표기되면 안 된다.
          */}
          {data.summary && (
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">AI가 생성한 요약</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-wrap text-sm leading-relaxed">{data.summary}</p>
                <p className="mt-3 text-xs text-muted-foreground">
                  이 문단은 아래 기사들을 바탕으로 AI가 생성했어요. 정확한 내용은 각 기사 원문을
                  확인해 주세요.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.items.map((item) => (
              <NewsCard key={item.link} item={item} keyword={data.keyword} />
            ))}
          </div>

          {/* The loop's call to action: the reader becomes the next searcher. */}
          <Card className="mt-8">
            <CardContent className="flex flex-wrap items-center justify-between gap-4 pt-6">
              <div>
                <p className="font-medium">이 클리핑은 헤드라이너로 만들었습니다</p>
                <p className="text-sm text-muted-foreground">
                  키워드 20개를 한 화면에서 추적하세요. 가입 없이 바로 검색됩니다.
                </p>
              </div>
              <Button asChild>
                <Link to="/">내 키워드로 검색하기</Link>
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
